import {decorate, inject, injectable} from '@yosmanyga/di';
import {action, observable, runInAction, makeObservable} from 'mobx';
import {Dispatcher} from '@yosmanyga/base';
import {LogType, RestorationApi} from '../../data';

class RestorationListController {
    loaded: boolean;
    collecting: boolean;
    inverted: boolean;
    total: number | undefined;
    logs: Array<LogType>;
    first: number | undefined;
    defaultLimit: number;
    limit: number | undefined;
    ended: boolean;

    selected: LogType | undefined;

    constructor(
        private dispatcher: Dispatcher,
        private restorationApi: RestorationApi
    ) {
        this.loaded = false;
        this.collecting = false;
        this.inverted = false;
        this.total = undefined;
        this.logs = [];
        this.first = undefined;
        this.defaultLimit = 100;
        this.limit = undefined;
        this.ended = false;
        this.selected = undefined;

        makeObservable(this, {
            loaded: observable,
            collecting: observable,
            inverted: observable,
            total: observable,
            logs: observable,
            first: observable,
            defaultLimit: observable,
            limit: observable,
            ended: observable,
            selected: observable,
            load: action,
            more: action,
            select: action
        });
    }

    load = async (inverted: boolean) => {
        this.inverted = inverted;
        this.total = await this.restorationApi.count();
        this.limit = this.calculateNewLimit(this.total);
        this.first = this.calculateNewFirst(this.total, this.limit);

        await this.collect();

        runInAction(() => {
            this.loaded = true;
        });
    };

    more = async () => {
        if (typeof this.first === 'undefined') {
            throw new Error('LogicException');
        }

        this.limit = this.calculateNewLimit(this.first);
        this.first = this.calculateNewFirst(this.first, this.limit);

        await this.collect();
    };

    select = (log: LogType) => {
        this.selected = log;

        this.dispatcher
            .retrieve<(log: LogType) => void>('restoration_list_item_selected')
            .forEach((service) => {
                service(log);
            });
    };

    private collect = async () => {
        if (this.collecting || this.ended) {
            return [];
        }

        if (this.limit === 0) {
            return [];
        }

        runInAction(() => {
            this.collecting = true;
        });

        if (
            typeof this.first === 'undefined' ||
            typeof this.limit === 'undefined'
        ) {
            throw new Error('LogicException');
        }

        const logs = await this.restorationApi.collect(this.first, this.limit);

        if (this.inverted) {
            logs.reverse();
        }

        runInAction(() => {
            this.logs = !this.inverted
                ? [...logs, ...this.logs] // Prepend
                : [...this.logs, ...logs] // Append
            this.collecting = false;
        });

        if (logs.length === 0) {
            this.ended = true;
        }
    };

    private calculateNewLimit = (first: number) => {
        return first - this.defaultLimit > 0 ? this.defaultLimit : first;
    };

    private calculateNewFirst = (first: number, limit: number) => {
        return first - limit;
    };
}

decorate(injectable(), RestorationListController);
decorate(inject(Dispatcher), RestorationListController, 0);
decorate(inject(RestorationApi), RestorationListController, 1);

export {RestorationListController};
